/**
 * Reports Store - Server-side data access via API routes
 *
 * All database operations are performed server-side through API routes.
 * This store uses fetch() calls to communicate with the Next.js API layer.
 *
 * Usage with Authentication:
 * ```typescript
 * const { createReport } = useReportsStore()
 * const { reportsUser, organization } = useAuth()
 *
 * // Create a report with current user context
 * const report = await createReport(reportFormData)
 * ```
 */

import { create } from "zustand"
import { persist } from "zustand/middleware"
import type { Report, ReportFormData, IJExtractedData } from "@/types/reports"
import { ReportStatus, ReportVersionStatus, UserRole, SectionMode } from "@/types/reports"
import { simpleGet, simplePost, simplePatch, simpleDelete } from "@/lib/apiUtils"

interface ReportsState {
	reports: Report[]
	selectedReport: Report | null
	isLoading: boolean
	error: string | null
	/** Expedient ID of an actively generating expedient (persists across navigation) */
	generatingExpedientId: string | null
}

interface ReportsActions {
	// Fetch operations
	fetchReports: () => Promise<void>
	fetchReportsByOrganization: (organizationId: string) => Promise<void>
	fetchReportById: (id: string) => Promise<void>
	fetchReportsByExpedientId: (expedientId: string) => Promise<Report[]>
	refreshReports: () => Promise<void>

	// CRUD operations
	createReport: (reportData: ReportFormData, authorId?: string, organizationId?: string, ijExtractedData?: IJExtractedData, includedOptionalSectionIds?: string[]) => Promise<Report>
	createStandaloneIJ: (reportData: ReportFormData, options?: { ijTemplateId?: string; ijExtractedData?: IJExtractedData; sectionMode?: SectionMode; includedOptionalSectionIds?: string[] }) => Promise<Report>
	createNonMinorReports: (reportData: ReportFormData, expedientId: string, options?: { bundleId?: string; pcapTemplateId?: string; pptTemplateId?: string; ijTemplateId?: string; ijExtractedData?: IJExtractedData; sectionMode?: SectionMode; includedOptionalSectionIds?: string[] }) => Promise<{ ijReport: Report; pcapReport: Report; pptReport: Report }>
	updateReport: (id: string, updates: Partial<Report>, userId?: string, versionParameters?: ReportFormData, versionStatus?: ReportVersionStatus, options?: { includeSectionSnapshots?: boolean; messages?: unknown[] }) => Promise<void>
	deleteReport: (id: string) => Promise<void>
	deleteExpedient: (expedientId: string) => Promise<void>
	publishReport: (reportId: string, userId: string, pdfAttachmentUrl?: string, pdfStoragePath?: string) => Promise<void>
	transferResponsibility: (reportId: string, toRole: UserRole, userId: string, comments?: string) => Promise<void>

	// Selection
	selectReport: (report: Report | null) => void

	// Generation tracking
	setGeneratingExpedientId: (id: string | null) => void

	// Utility
	clearError: () => void
	setLoading: (loading: boolean) => void
	clearAllData: () => void
	hasUnsavedChanges: () => boolean
	saveAllPendingData: () => Promise<void>
}

type ReportsStore = ReportsState & ReportsActions

export const useReportsStore = create<ReportsStore>()(
	persist(
		(set, get) => ({
			// Initial state
			reports: [],
			selectedReport: null,
			isLoading: false,
			error: null,
			generatingExpedientId: null,

			// Fetch operations
			fetchReports: async () => {
				set({ isLoading: true, error: null })
				try {
					const { reports } = await simpleGet<{ reports: Report[]; total: number }>("/api/reports?limit=200")
					set({ reports, isLoading: false })
				} catch (error) {
					set({
						error: error instanceof Error ? error.message : "Failed to fetch reports",
						isLoading: false,
					})
				}
			},

			fetchReportsByOrganization: async (organizationId: string) => {
				set({ isLoading: true, error: null })
				try {
					const { reports } = await simpleGet<{ reports: Report[]; total: number }>(`/api/reports?organizationId=${encodeURIComponent(organizationId)}&limit=200`)
					set({ reports, isLoading: false })
				} catch (error) {
					set({
						error: error instanceof Error ? error.message : "Failed to fetch reports by organization",
						isLoading: false,
					})
				}
			},

			fetchReportsByExpedientId: async (expedientId: string) => {
				try {
					const { reports } = await simpleGet<{ reports: Report[]; total: number }>(`/api/reports?expedientId=${encodeURIComponent(expedientId)}&limit=200`)
					return reports
				} catch (error) {
					console.error("Error fetching reports by expedientId:", error)
					throw error
				}
			},

			refreshReports: async () => {
				const state = get()
				if (state.reports.length > 0) {
					await state.fetchReports()
				}
			},

			fetchReportById: async (id: string) => {
				set({ isLoading: true, error: null })
				try {
					const report = await simpleGet<Report>(`/api/reports/${id}`)
					if (report) {
						set({ selectedReport: report, isLoading: false })
					} else {
						set({ error: "Report not found", isLoading: false })
					}
				} catch (error) {
					set({
						error: error instanceof Error ? error.message : "Failed to fetch report",
						isLoading: false,
					})
				}
			},

			// CRUD operations
			createReport: async (reportData: ReportFormData, _authorId?: string, _organizationId?: string, ijExtractedData?: IJExtractedData, includedOptionalSectionIds?: string[]) => {
				set({ isLoading: true, error: null })
				try {
					const newReport = await simplePost<{ reportData: ReportFormData; ijExtractedData?: IJExtractedData; includedOptionalSectionIds?: string[] }, Report>(
						"/api/reports",
						{ reportData, ...(ijExtractedData ? { ijExtractedData } : {}), ...(includedOptionalSectionIds?.length ? { includedOptionalSectionIds } : {}) },
						{ wafSafe: true },
					)

					set((state) => ({
						reports: [...state.reports, newReport],
						selectedReport: newReport,
						isLoading: false,
					}))

					return newReport
				} catch (error) {
					set({
						error: error instanceof Error ? error.message : "Failed to create report",
						isLoading: false,
					})
					throw error
				}
			},

			createStandaloneIJ: async (reportData: ReportFormData, options?: { ijTemplateId?: string; ijExtractedData?: IJExtractedData; sectionMode?: SectionMode; includedOptionalSectionIds?: string[] }) => {
				set({ isLoading: true, error: null })
				try {
					const { ijTemplateId, ijExtractedData, sectionMode, includedOptionalSectionIds } = options || {}
					const newReport = await simplePost<
						{ standaloneIJ: true; reportData: ReportFormData; ijTemplateId?: string; ijExtractedData?: IJExtractedData; sectionMode?: SectionMode; includedOptionalSectionIds?: string[] },
						Report
					>("/api/reports", {
						standaloneIJ: true,
						reportData,
						...(ijTemplateId ? { ijTemplateId } : {}),
						...(ijExtractedData ? { ijExtractedData } : {}),
						...(sectionMode ? { sectionMode } : {}),
						...(includedOptionalSectionIds?.length ? { includedOptionalSectionIds } : {}),
					}, { wafSafe: true })

					set((state) => ({
						reports: [...state.reports, newReport],
						selectedReport: newReport,
						isLoading: false,
					}))

					return newReport
				} catch (error) {
					set({
						error: error instanceof Error ? error.message : "Failed to create standalone IJ report",
						isLoading: false,
					})
					throw error
				}
			},

			createNonMinorReports: async (reportData: ReportFormData, expedientId: string, options?: { bundleId?: string; pcapTemplateId?: string; pptTemplateId?: string; ijTemplateId?: string; ijExtractedData?: IJExtractedData; sectionMode?: SectionMode; includedOptionalSectionIds?: string[] }) => {
				set({ isLoading: true, error: null })
				try {
					const { bundleId, pcapTemplateId, pptTemplateId, ijTemplateId, ijExtractedData, sectionMode, includedOptionalSectionIds } = options || {}
					const result = await simplePost<
						{ nonMinor: true; expedientId: string; reportData: ReportFormData; bundleId?: string; pcapTemplateId?: string; pptTemplateId?: string; ijTemplateId?: string; ijExtractedData?: IJExtractedData; sectionMode?: SectionMode; includedOptionalSectionIds?: string[] },
						{ ijReport: Report; pcapReport: Report; pptReport: Report }
					>("/api/reports", {
						nonMinor: true,
						expedientId,
						reportData,
						...(bundleId ? { bundleId } : {}),
						...(pcapTemplateId ? { pcapTemplateId } : {}),
						...(pptTemplateId ? { pptTemplateId } : {}),
						...(ijTemplateId ? { ijTemplateId } : {}),
						...(ijExtractedData ? { ijExtractedData } : {}),
						...(sectionMode ? { sectionMode } : {}),
						...(includedOptionalSectionIds?.length ? { includedOptionalSectionIds } : {}),
					}, { wafSafe: true })

					set((state) => ({
						reports: [...state.reports, result.ijReport, result.pcapReport, result.pptReport],
						isLoading: false,
					}))

					return result
				} catch (error) {
					set({
						error: error instanceof Error ? error.message : "Failed to create non-minor reports",
						isLoading: false,
					})
					throw error
				}
			},

			updateReport: async (id: string, updates: Partial<Report>, _userId?: string, versionParameters?: ReportFormData, versionStatus?: ReportVersionStatus, options?: { includeSectionSnapshots?: boolean; messages?: unknown[] }) => {
				set({ isLoading: true, error: null })
				try {
					const result = await simplePatch<
						{ updates: Partial<Report>; versionParameters?: ReportFormData; versionStatus?: ReportVersionStatus; includeSectionSnapshots?: boolean; messages?: unknown[] },
						{ success: boolean; version: number }
					>(`/api/reports/${id}`, {
						updates,
						versionParameters,
						versionStatus,
						...(options?.includeSectionSnapshots ? { includeSectionSnapshots: true } : {}),
						...(options?.messages ? { messages: options.messages } : {}),
					}, { wafSafe: true })

					// Update local state
					set((state) => {
						const updatedReports = state.reports.map((report) =>
							report.id === id
								? {
										...report,
										...updates,
										updatedAt: new Date() as unknown as Report["updatedAt"],
										version: result.version,
									}
								: report,
						)

						const updatedReport = updatedReports.find((r) => r.id === id)

						return {
							reports: updatedReports,
							selectedReport: updatedReport || state.selectedReport,
							isLoading: false,
						}
					})
				} catch (error) {
					console.error("Update report error:", error)
					set({
						error: error instanceof Error ? error.message : "Failed to update report",
						isLoading: false,
					})
				}
			},

			deleteReport: async (id: string) => {
				set({ isLoading: true, error: null })
				try {
					await simpleDelete(`/api/reports/${id}`)

					set((state) => ({
						reports: state.reports.filter((report) => report.id !== id),
						selectedReport: state.selectedReport?.id === id ? null : state.selectedReport,
						isLoading: false,
					}))
				} catch (error) {
					console.error("Error deleting report:", error)
					set({
						error: error instanceof Error ? error.message : "Failed to delete report",
						isLoading: false,
					})
					throw error
				}
			},

			deleteExpedient: async (expedientId: string) => {
				set({ isLoading: true, error: null })
				try {
					await simpleDelete(`/api/reports/expedient/${expedientId}`)

					set((state) => ({
						reports: state.reports.filter(
							(r) => !(r.expedientId === expedientId && r.contractScope === "non_minor")
						),
						selectedReport:
							state.selectedReport?.expedientId === expedientId ? null : state.selectedReport,
						isLoading: false,
					}))
				} catch (error) {
					console.error("Error deleting expedient:", error)
					set({
						error: error instanceof Error ? error.message : "Failed to delete expedient",
						isLoading: false,
					})
					throw error
				}
			},

			publishReport: async (reportId: string, _userId: string, pdfAttachmentUrl?: string, pdfStoragePath?: string) => {
				set({ isLoading: true, error: null })
				try {
					await simplePost(`/api/reports/${reportId}/publish`, {
						pdfAttachmentUrl,
						pdfStoragePath,
					})

					// Update local state
					set((state) => {
						const updatedReports = state.reports.map((report) =>
							report.id === reportId
								? {
										...report,
										status: ReportStatus.PUBLISHED,
										updatedAt: new Date() as unknown as Report["updatedAt"],
										...(pdfAttachmentUrl ? { pdfAttachmentUrl } : {}),
									...(pdfStoragePath ? { pdfStoragePath } : {}),
									}
								: report,
						)

						const updatedReport = updatedReports.find((r) => r.id === reportId)

						return {
							reports: updatedReports,
							selectedReport: updatedReport || state.selectedReport,
							isLoading: false,
						}
					})
				} catch (error) {
					console.error("Publish report error:", error)
					set({
						error: error instanceof Error ? error.message : "Failed to publish report",
						isLoading: false,
					})
					throw error
				}
			},

			transferResponsibility: async (reportId: string, toRole: UserRole, _userId: string, comments?: string) => {
				set({ isLoading: true, error: null })
				try {
					await simplePost(`/api/reports/${reportId}/transfer`, {
						toRole,
						comments,
					}, { wafSafe: true })

					// Update local state
					set((state) => {
						const updatedReports = state.reports.map((report) =>
							report.id === reportId
								? {
										...report,
										currentResponsible: toRole,
										status: toRole === UserRole.CONTRACTACIO ? ReportStatus.IN_REVIEW : report.status,
										updatedAt: new Date() as unknown as Report["updatedAt"],
									}
								: report,
						)

						const updatedReport = updatedReports.find((r) => r.id === reportId)

						return {
							reports: updatedReports,
							selectedReport: updatedReport || state.selectedReport,
							isLoading: false,
						}
					})
				} catch (error) {
					console.error("Transfer responsibility error:", error)
					set({
						error: error instanceof Error ? error.message : "Failed to transfer responsibility",
						isLoading: false,
					})
					throw error
				}
			},

			// Selection
			selectReport: (report: Report | null) => {
				set({ selectedReport: report })
			},

			// Generation tracking
			setGeneratingExpedientId: (id: string | null) => {
				set({ generatingExpedientId: id })
			},

			// Utility
			clearError: () => {
				set({ error: null })
			},

			setLoading: (loading: boolean) => {
				set({ isLoading: loading })
			},

			clearAllData: () => {
				set({
					reports: [],
					selectedReport: null,
					isLoading: false,
					error: null,
				})
				if (typeof window !== "undefined") {
					localStorage.removeItem("reports-store")
				}
			},

			hasUnsavedChanges: (): boolean => {
				return false
			},

			saveAllPendingData: async (): Promise<void> => {
				// Wait for any in-flight operations to settle
				if (get().isLoading) {
					await new Promise<void>((resolve) => {
						const unsubscribe = useReportsStore.subscribe((state) => {
							if (!state.isLoading) {
								unsubscribe()
								resolve()
							}
						})
						// Timeout after 3s to avoid hanging
						setTimeout(() => { unsubscribe(); resolve() }, 3000)
					})
				}
				set({ isLoading: false, error: null })
			},
		}),
		{
			name: "reports-store",
			partialize: (state) => ({
				// Exclude heavy content fields to avoid exceeding localStorage limits
				reports: state.reports.map(({ content, ...rest }) => rest),
				selectedReport: state.selectedReport ? (({ content, ...rest }) => rest)(state.selectedReport) : null,
			}),
			version: 1,
			migrate: (persistedState: unknown, version: number) => {
				const state = persistedState as Partial<ReportsState>
				if (version === 0) {
					if (state.reports) {
						state.reports = state.reports.map((r) => ({
							...r,
							currentResponsible: r.currentResponsible || UserRole.GERENCIA,
						}))
					}
					if (state.selectedReport && !state.selectedReport.currentResponsible) {
						state.selectedReport = {
							...state.selectedReport,
							currentResponsible: UserRole.GERENCIA,
						}
					}
				}
				return state as ReportsState & ReportsActions
			},
		},
	),
)
